import { useCallback, useState } from 'react';
import { useMatch, useNavigate } from 'react-router-dom';
import { HoldToRecordButton, useHoldToRecord } from '../../contexts/HoldToRecordContext';
import { transcriptHasClinicalSignal } from '../../utils/clinicalContent';
import { doctorGeneratingPath, ROUTES } from '../../constants/routes';
import { useLanguage } from '../../contexts/LanguageContext';
import { doctorGetPatient, doctorListPatients } from '../services/doctorApi';
import { useDoctorSession } from '../contexts/DoctorSessionContext';
import { matchDoctorPatientFromTranscript } from '../utils/matchDoctorPatient';

/**
 * Same nurse HoldToRecord FAB + overlay UI.
 * Routes the finished transcript into the doctor note pipeline.
 * Non-clinical speech is shown in red by HoldToRecord (same as nurse).
 */
export default function DoctorRecordFAB({
  patientId: patientIdProp = null,
  patientLabel: patientLabelProp = null,
}) {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { overlayOpen, showOverlayError } = useHoldToRecord();
  const { trialExhausted, setDraftNote, showToast } = useDoctorSession();
  const patientMatch = useMatch({ path: '/doctor/patients/:id', end: true });
  const patientId = patientIdProp || patientMatch?.params?.id || null;
  const [picker, setPicker] = useState(null);

  const goToGenerating = useCallback(
    (resolvedPatient, transcript, durationSec = 1) => {
      setDraftNote({
        patientId: resolvedPatient.id,
        patient: resolvedPatient,
        transcript,
        audioDurationSeconds: durationSec,
      });
      navigate(doctorGeneratingPath(resolvedPatient.id));
    },
    [navigate, setDraftNote]
  );

  const handleTranscript = useCallback(
    async (transcript, meta = {}) => {
      if (trialExhausted) {
        navigate(ROUTES.DOCTOR_PAYWALL);
        return;
      }

      const trimmed = String(transcript || '').trim();
      const durationSec = meta.recordingDurationSeconds || 1;
      if (!trimmed) {
        // HoldToRecord already surfaces noSpeechDetected in red on the overlay.
        return;
      }
      if (!transcriptHasClinicalSignal(trimmed)) {
        // Safety net — HoldToRecord normally catches this and shows red overlay text.
        showOverlayError(t('noClinicalContent'), {
          patientId,
          patientLabel: patientLabelProp || null,
        });
        return;
      }

      if (patientId) {
        try {
          const full = await doctorGetPatient(patientId);
          goToGenerating(full, trimmed, durationSec);
        } catch (err) {
          showToast(err.message || t('doctorGenerateFailed'));
        }
        return;
      }

      try {
        const data = await doctorListPatients();
        const patients = data.patients || [];
        if (!patients.length) {
          showToast(t('doctorNoPatientsYet'));
          return;
        }
        const { best, candidates } = matchDoctorPatientFromTranscript(patients, trimmed);
        if (best) {
          goToGenerating(best, trimmed, durationSec);
          return;
        }
        setPicker({
          transcript: trimmed,
          durationSec,
          candidates: candidates.length ? candidates : patients,
        });
      } catch (err) {
        showToast(err.message || t('doctorGenerateFailed'));
      }
    },
    [
      goToGenerating,
      navigate,
      patientId,
      patientLabelProp,
      showOverlayError,
      showToast,
      t,
      trialExhausted,
    ]
  );

  if (trialExhausted) {
    return (
      <button
        type="button"
        onClick={() => navigate(ROUTES.DOCTOR_PAYWALL)}
        aria-label={t('doctorNewNote')}
        className="fixed bottom-[calc(6.25rem+env(safe-area-inset-bottom,0px))] left-1/2 z-[100] flex h-[72px] w-[72px] -translate-x-1/2 items-center justify-center rounded-full bg-primary text-white opacity-60 shadow-fab"
      >
        <span className="text-xs font-bold">Pro</span>
      </button>
    );
  }

  return (
    <>
      <HoldToRecordButton
        patientId={patientId}
        size="fab"
        dataTour="doctor-record-mic"
        holdOptions={{
          onTranscript: handleTranscript,
          patientLabel: patientLabelProp || null,
        }}
        className={`${overlayOpen ? 'opacity-0 z-[240]' : ''} !bottom-[calc(6.25rem+env(safe-area-inset-bottom,0px))]`}
      />

      {picker && (
        <div className="fixed inset-0 z-[300] flex flex-col justify-end bg-black/50">
          <div className="rounded-t-2xl bg-white px-4 pb-[max(1.5rem,env(safe-area-inset-bottom))] pt-4">
            <p className="mb-1 text-base font-bold text-slate-900">{t('doctorWhoIsThisNote')}</p>
            <p className="mb-4 text-sm text-gray-500">{t('doctorSelectPatientFromTranscript')}</p>
            <div className="max-h-[50vh] space-y-2 overflow-y-auto">
              {picker.candidates.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => {
                    const { transcript, durationSec } = picker;
                    setPicker(null);
                    goToGenerating(p, transcript, durationSec);
                  }}
                  className="flex w-full items-center justify-between rounded-xl border border-gray-200 px-4 py-3 text-left"
                >
                  <span className="font-semibold text-slate-900">{p.full_name}</span>
                  <span className="text-xs text-gray-400">
                    {[p.age, p.gender].filter(Boolean).join(' · ')}
                  </span>
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={() => setPicker(null)}
              className="mt-3 w-full py-3 text-sm font-medium text-gray-500"
            >
              {t('cancel')}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
